/**
 * Generating a triangle mesh from design material SDF patterns with explicit mitering.
 * @module
 */
import assert from "assert";
import degreeToRadian from "common/utils/degreeToRadian";
import { aabb2Center, aabb2Height, aabb2Width } from "geom/aabb2";
import type { Face } from "geom/brep";
import { faceOrNullCopy, faceNew } from "geom/brep";
import { addConvexPlanesToBSPLeaf, buildBSPNode, buildEdgePolygon } from "geom/bsp";
import { splitFacesBSP } from "geom/bspClipping";
import type { NodeBSP, PlaneBSP, PolygonBSP } from "geom/bspTypes";
import { DIST_EPSILON } from "geom/core";
import type { Line2Segment } from "geom/line2";
import type { Line3Segment } from "geom/line3";
import type { Mat3 } from "geom/mat3";
import { mat3Dot, mat3IsMirroringAffineTransform, mat3SolveAffineTransformFromPoints } from "geom/mat3";
import { mat4Dot, mat4Ident, mat4Mult, mat4Rotate, mat4Scale, mat4Translate } from "geom/mat4";
import type { Mesh3, Mesh3Instances } from "geom/mesh";
import { getFacesFromMesh, mesh3New } from "geom/mesh";
import type { Plane } from "geom/plane";
import {
  planeCopy,
  planeEval,
  planeNew,
  planeNewXY,
  planeNormal,
  planeProjectPolyLine3,
  planeReverse,
  PlaneSide,
} from "geom/plane";
import { planeBSPFromPlane } from "geom/planeBSP";
import { PolygonRoundingMode } from "geom/polyclip/polyClip";
import { polygonIntersectSimple, polygonSubtract, polygonUnion } from "geom/polygon";
import {
  polyLine2AABB2,
  polyLine2Area,
  polyLine2Centroid,
  polyLine2Copy,
  polyLine2EdgeCount,
  polyLine2EdgeEnd,
  polyLine2EdgesCollinearToLine,
  polyLine2EdgeStart,
  polyLine2FromAABB2,
  polyLine2IsClosedRect,
  polyLine2IsConvex,
  polyLine2MakeAxisAlignedRect,
  polyLine2Move,
  polyLine2MoveEdges,
  polyLine2NonUniformScale,
  polyLine2Rotate,
  polyLine2SetCW,
  polyLine2Transform,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { clipFacesAgainstPlanes } from "geom/sutherlandHodgman";
import { mesh2ToMesh3, tessFaces3, tessPolyLine2 } from "geom/tess";
import { pushArray } from "geom/utils";
import type { UVTransformation } from "geom/uv";
import { applyUVs, UVGeneration } from "geom/uv";
import { vec2AngleBetween, vec2Dist, vec2DistSquared, vec2Lerp, vec2Normalize, vec2Rotate, vec2Sub } from "geom/vec2";
import { vec3Dot } from "geom/vec3";
import { weldPolyLine2s } from "geom/welding";
import {
  cellTopDownBoundingBoxX,
  cellTopDownBoundingBoxY,
  hasSdfDistortions,
  sdfGridToUvTransform,
} from "../designMaterialParams";
import type {
  SdfCommonParamsPreprocessed,
  SdfDistortion,
  SdfParamsPreprocessed,
  SdfPatternPreprocessed,
} from "../designMaterialTypes";
import type { DebugCallback } from "./debug";
import { DebugCallbackType, getDebugId, incDebugId } from "./debug";
import { mergeCoplanarMeshFaces, miterPlaneSide } from "./facePostProcessing";
import { clearCellGeometryCache, fetchCellGeometry } from "./generateCellGeometry";
import type { GeoBuilder } from "./geoBuilder";
import { compactMesh3FromGeoBuilder, geoBuilderAppend, geoBuilderIsEmpty, geoBuilderNew } from "./geoBuilder";
import { DISTORTED_SDF_MIN_VERTEX_INTERVAL, distortSDFVertex } from "./sdfDistortion";
import { sdfCellBoundariesInAABB, sdfTileLocationsInAABB } from "./sdfLayout";
import type { AdjustGeoType } from "./utils";

export enum PatternIntersectionType {
  OUTSIDE = 0,
  ORIGINAL = 1,
  RECTANGULAR = 2,
  CONVEX = 3,
  CONCAVE = 4,
}

// Defines miter used by the affected faces
export interface FaceFaceMiter {
  miterPlane: Plane;
  segments: Line3Segment[];

  /**
   * Inside corners are where our 1D wall system creates material void at the meeting point
   *
   *       Inside corner              Outside corner            Legend
   *                                                              _  or |    1D wall
   *       ...........                       ^                       .       material
   *       .          .                      |                   <-  or |    wall normal
   *       ___________.....          _________________                  V
   *            |     |   .          .           .   |
   *            v     |   .          ................|
   *                <-|   .                      .   |
   *                  |   .                      .   |->
   *                  |   .                      .   |
   *                  |....                      ....|
   *
   */
  isInsideCorner: boolean;
}

// Groups all miters by face
export type FaceMiters = { [faceIndex: number]: FaceFaceMiter[] };

// Resolves an individual miter, for a particular face
export interface FaceMiter {
  miterPlane: Plane;
  line: Line2Segment;
  isInsideCorner: boolean;
}

export type FaceInstances = Mesh3Instances[];

export interface GenerateMesh3WithinFacesOptions {
  mergeCoplanarFaces: boolean;
  generatePatternInstances: boolean;
  generateMissingNormals: boolean;
  debugCallback?: DebugCallback;
  geometryGenerationDebugIdFilter?: number[];
  wrapSizeU?: number;
  wrapSizeV?: number;
  expandCellsAtInsideCornerMiters?: boolean;
}

enum SdfDirection {
  NONE,
  X,
  Y,
}

function getMaxHorizontalDistortionOffset(distortions: SdfDistortion[]) {
  let maxOffset = 0;
  for (const distortion of distortions) {
    if (distortion.enabled) {
      const x = distortion.strengthX;
      const y = distortion.strengthY;
      maxOffset += Math.sqrt(x * x + y * y);
    }
  }
  return maxOffset;
}

function getCellLooseBoundingRect(
  sdfCommon: SdfCommonParamsPreprocessed,
  distortions: SdfDistortion[],
  preprocessedPattern: SdfPatternWithCellGeometry,
  cell: OutputCell,
): PolyLine2 {
  const pattern = preprocessedPattern.pattern;
  // The cell will get clipped by cellLooseBoundingRect, so add tolerance around the tight bounding box.
  // The tight bounding box in the OutputCell doesn't have modifiers applied that may grow it outside the bounds of the generating rectangle.
  const maxDistortionOffset = getMaxHorizontalDistortionOffset(distortions);
  let clippingToleranceScaleX = 1;
  if (preprocessedPattern.direction !== SdfDirection.X) {
    clippingToleranceScaleX = (cellTopDownBoundingBoxX(pattern) + maxDistortionOffset + 0.1) / pattern.xSize;
  }
  let clippingToleranceScaleY = 1;
  if (preprocessedPattern.direction !== SdfDirection.Y) {
    clippingToleranceScaleY = (cellTopDownBoundingBoxY(sdfCommon, pattern) + maxDistortionOffset + 0.1) / pattern.ySize;
  }

  const slopeRotation = sdfCommon.slopeAxis === "Z" && sdfCommon.randomTilesMode === "None" ? pattern.slope : 0;
  const rotationRadians = degreeToRadian(pattern.rotation + slopeRotation);

  const cellLooseBoundingRect = polyLine2Move(
    polyLine2Rotate(
      polyLine2NonUniformScale(polyLine2Copy(cell.centeredBoundary), clippingToleranceScaleX, clippingToleranceScaleY),
      rotationRadians,
    ),
    cell.uCenter,
    cell.vCenter,
  );

  return cellLooseBoundingRect;
}

interface SdfPatternWithCellGeometry {
  pattern: SdfPatternPreprocessed;
  cellGeometry: GeoBuilder;
  cellMesh: Mesh3;
  direction: SdfDirection;
  gridToUvTransform: Mat3;
}

function preprocessSdfPatterns(
  sdfCommon: SdfCommonParamsPreprocessed,
  patterns: SdfPatternPreprocessed[],
  sdfDistorted: boolean,
): SdfPatternWithCellGeometry[] {
  const preprocessedPatterns: SdfPatternWithCellGeometry[] = [];

  for (let patternIndex = 0; patternIndex < patterns.length; patternIndex++) {
    const pattern: SdfPatternPreprocessed = patterns[patternIndex];

    // Detect materials that are continuous in one direction (like siding) and bump up distance and size so
    // that we generate less geometry.
    let direction = SdfDirection.NONE;
    if (pattern.continuousX && pattern.continuousY) {
      direction = SdfDirection.X;
      console.warn(`Material is set as continuous in both x and y direction`);
    } else if (pattern.continuousX) {
      direction = SdfDirection.X;
    } else if (pattern.continuousY) {
      direction = SdfDirection.Y;
    }

    const cellGeometry = fetchCellGeometry(
      pattern,
      sdfCommon,
      sdfDistorted,
      sdfDistorted ? DISTORTED_SDF_MIN_VERTEX_INTERVAL : Infinity,
    );

    const vertices = new Float32Array(cellGeometry.vertices);
    const vertexCount = cellGeometry.vertices.length / 3;

    const uvs = new Float32Array(2 * vertexCount);
    applyUVs(uvs, vertices, planeNewXY(), UVGeneration.GLOBAL_RECT_UV);

    const cellMesh = mesh3New(vertices, new Uint32Array(cellGeometry.indices), undefined, uvs);
    const gridToUvTransform = sdfGridToUvTransform(pattern, true);

    preprocessedPatterns.push({
      pattern,
      cellGeometry,
      cellMesh,
      direction,
      gridToUvTransform,
    });
  }

  clearCellGeometryCache();

  return preprocessedPatterns;
}

function computeClipBSP(
  intersectionType: PatternIntersectionType,
  clipLoop: PolyLine2,
  plane: Plane,
  nX: number,
  nY: number,
  nZ: number,
  cellEdgeDirU: number,
  cellEdgeDirV: number,
  cellCenterU: number,
  cellCenterV: number,
) {
  if (intersectionType === PatternIntersectionType.CONVEX) {
    const planes: PlaneBSP[] = [];

    const l = polyLine2EdgeCount(clipLoop);
    for (let i = 0; i < l; ++i) {
      const [edgeStartU, edgeStartV] = polyLine2EdgeStart(clipLoop, i);
      const [edgeEndU, edgeEndV] = polyLine2EdgeEnd(clipLoop, i);
      const [currentX, currentY, currentZ] = planeEval(plane, edgeStartU, edgeStartV);
      const [nextX, nextY, nextZ] = planeEval(plane, edgeEndU, edgeEndV);

      const clipPlane = planeNew(
        currentX,
        currentY,
        currentZ,
        nextX - currentX,
        nextY - currentY,
        nextZ - currentZ,
        -nX,
        -nY,
        -nZ,
      );

      planes.push(planeBSPFromPlane(clipPlane));
    }

    return addConvexPlanesToBSPLeaf(planes);
  } else {
    const clipPolygons: PolygonBSP[] = [];

    const perpendicularClipIndices: number[] = [];
    let farthestPerpendicularClipDistance = -1;
    let farthestPerpendicularClipIndex: number | undefined = undefined;

    const l = polyLine2EdgeCount(clipLoop);
    for (let edgeIndex = 0; edgeIndex < l; ++edgeIndex) {
      const [edgeStartU, edgeStartV] = polyLine2EdgeStart(clipLoop, edgeIndex);
      const [edgeEndU, edgeEndV] = polyLine2EdgeEnd(clipLoop, edgeIndex);
      const [currentX, currentY, currentZ] = planeEval(plane, edgeStartU, edgeStartV);
      const [nextX, nextY, nextZ] = planeEval(plane, edgeEndU, edgeEndV);

      const clipPolygon = buildEdgePolygon(currentX, currentY, currentZ, nextX, nextY, nextZ, -nX, -nY, -nZ);

      if (!clipPolygon) {
        return null;
      }

      // Determine if it's clipping perpendicularly to the direction
      const angle = Math.abs(
        vec2AngleBetween(edgeEndU - edgeStartU, edgeEndV - edgeStartV, cellEdgeDirU, cellEdgeDirV),
      );

      if (Math.abs(angle - 0.5 * Math.PI) < 0.1) {
        perpendicularClipIndices.push(edgeIndex);

        const [edgeMiddleU, edgeMiddleV] = vec2Lerp(edgeStartU, edgeStartV, edgeEndU, edgeEndV, 0.5);
        const distanceFromPatternCenter = vec2Dist(edgeMiddleU, edgeMiddleV, cellCenterU, cellCenterV);
        if (farthestPerpendicularClipDistance < distanceFromPatternCenter) {
          farthestPerpendicularClipIndex = perpendicularClipIndices.length - 1;
          farthestPerpendicularClipDistance = distanceFromPatternCenter;
        }
      }

      clipPolygons.push(clipPolygon);
    }

    // Rotate perpendicular indices so they start from the farthest
    perpendicularClipIndices
      .slice(farthestPerpendicularClipIndex, perpendicularClipIndices.length)
      .concat(perpendicularClipIndices.slice(0, farthestPerpendicularClipIndex));

    // Sort clipping polygons:
    // - first those perpendicular to the pattern direction
    // - then the remaining, in order
    const sortedClipPolygons: PolygonBSP[] = [];
    for (const clipIndex of perpendicularClipIndices) {
      sortedClipPolygons.push(clipPolygons[clipIndex]);
    }

    const perpendicularClipIndexSet = new Set(perpendicularClipIndices);
    for (let clipIndex = 0; clipIndex < clipPolygons.length; clipIndex++) {
      if (!perpendicularClipIndexSet.has(clipIndex)) {
        sortedClipPolygons.push(clipPolygons[clipIndex]);
      }
    }

    return buildBSPNode({ polygons: sortedClipPolygons });
  }
}

function mat4PlaneToWorld(plane: Plane, nX: number, nY: number, nZ: number) {
  const planeToWorldMat = mat4Ident();
  planeToWorldMat[0] = plane.uX;
  planeToWorldMat[1] = plane.uY;
  planeToWorldMat[2] = plane.uZ;
  planeToWorldMat[4] = plane.vX;
  planeToWorldMat[5] = plane.vY;
  planeToWorldMat[6] = plane.vZ;
  planeToWorldMat[8] = nX;
  planeToWorldMat[9] = nY;
  planeToWorldMat[10] = nZ;
  planeToWorldMat[12] = plane.originX;
  planeToWorldMat[13] = plane.originY;
  planeToWorldMat[14] = plane.originZ;
  return planeToWorldMat;
}

interface MergedTileSpan {
  rowIndex: number;
  minTileInRow: number;
  maxTileInRow: number;
}

interface OutputCell {
  centeredBoundary: PolyLine2;
  randomValue: number;
  uCenter: number; // In global UV space
  vCenter: number; // In global UV space
  geometryXScale: number;
  geometryYScale: number;
}

/**
 * This function converts a design material SDF into tessellated geometry.
 * There are up to four independent SDF patterns in each design material.
 * Each SDF pattern divides the UV space into a rotated grid, which is used as the basis of placing SDF cells.
 * The shape of the cell is generated by generateCellGeometry, and functions from sdfLayout are used to
 * determine the locations in UV space where the cells are placed.
 * After the geometry for the cells has been generated, it is clipped against the base geometry in UV space.
 * The resulting cell is transformed into world space and added to the geometry that gets returned.
 *
 * @param faces Base geometry
 * @param adjustBaseGeo Whether to adjust base geometry based on design material thickness
 * @param params Design material parameters
 * @param uvGen UV generation mode
 * @param materialTransformations UV transformations
 * @param miters
 */
export function generateMesh3WithinFaces(
  faces: Face[],
  adjustBaseGeo: AdjustGeoType,
  sdf: SdfParamsPreprocessed,
  uvGen: UVGeneration | undefined,
  materialTransformations: UVTransformation | UVTransformation[] | undefined,
  miters: FaceMiters | undefined,
  {
    mergeCoplanarFaces = false,
    generatePatternInstances = true,
    generateMissingNormals = false,
    expandCellsAtInsideCornerMiters = false,
    debugCallback,
    geometryGenerationDebugIdFilter,
    wrapSizeU,
    wrapSizeV,
  }: GenerateMesh3WithinFacesOptions,
): [(Mesh3 | undefined)[], number[], (FaceInstances | undefined)[]] {
  const faceMeshes: (Mesh3 | undefined)[] = [];
  const faceInwardPush: number[] = [];
  const faceInstances: (FaceInstances | undefined)[] = [];

  function debugPatternKey(key: string, i: number, intersectionType: PatternIntersectionType | null, itemType: string) {
    const optionalIntersectionType =
      intersectionType !== null ? PatternIntersectionType[intersectionType].toLowerCase() + "-" : "";
    return `${key}-${i}-${optionalIntersectionType}${itemType}`;
  }

  const clippingTolerance = 0.01; // in inches

  function clippedAtEndsOnly(clippedLoop: PolyLine2, spanRect: PolyLine2, tol: number = DIST_EPSILON) {
    if (!polyLine2IsClosedRect(clippedLoop)) {
      return false;
    }

    const loopDimSq1 = vec2DistSquared(clippedLoop[0], clippedLoop[1], clippedLoop[2], clippedLoop[3]);
    const loopDimSq2 = vec2DistSquared(clippedLoop[2], clippedLoop[3], clippedLoop[4], clippedLoop[5]);
    const spanDimSq1 = vec2DistSquared(spanRect[0], spanRect[1], spanRect[2], spanRect[3]);
    const spanDimSq2 = vec2DistSquared(spanRect[2], spanRect[3], spanRect[4], spanRect[5]);

    return (
      Math.abs(loopDimSq1 - spanDimSq1) < tol ||
      Math.abs(loopDimSq1 - spanDimSq2) < tol ||
      Math.abs(loopDimSq2 - spanDimSq1) < tol ||
      Math.abs(loopDimSq2 - spanDimSq2) < tol
    );
  }

  // Cache patterns, geometries and directions
  const sdfDistorted: boolean = hasSdfDistortions(sdf.distortions);
  const preprocessedPatterns: SdfPatternWithCellGeometry[] = preprocessSdfPatterns(
    sdf.common,
    sdf.patterns,
    sdfDistorted,
  );

  //------------------------------------------------------------
  // Generate patterns and geometries
  //------------------------------------------------------------
  let cellIndex = 0;
  for (let faceIndex = 0; faceIndex < faces.length; faceIndex++) {
    const face = faces[faceIndex];

    if (debugCallback && geometryGenerationDebugIdFilter && !geometryGenerationDebugIdFilter.includes(getDebugId())) {
      faceMeshes.push(undefined);
      faceInwardPush.push(0);
      faceInstances.push(undefined);

      incDebugId();
      continue;
    }

    const faceGeoBuilder = geoBuilderNew();

    const facePatternInstances: FaceInstances = [];
    faceInstances.push(facePatternInstances);
    for (let patternIndex = 0; patternIndex < preprocessedPatterns.length; patternIndex++) {
      facePatternInstances.push({ mesh: preprocessedPatterns[patternIndex].cellMesh, matrices: [] });
    }

    const outputCells: { [patternIndex: number]: { [cellKey: string]: OutputCell } } = {};

    // Only needed for debug callback
    const inputCellFaces = {};
    const outputCellFaces = {};
    const patternSpanFaces = {};
    const patternLoopFaces = {};
    const patternGeometries = {};

    const [nX, nY, nZ] = planeNormal(face.plane);
    const faceUvwToWorldMat = mat4PlaneToWorld(face.plane, nX, nY, nZ);
    const faceOuterLoop = face.loops[0];
    const faceInnerLoops = face.loops.slice(1);

    // Face UV to global UV mapping
    const faceRect = polyLine2AABB2(faceOuterLoop);

    if (aabb2Width(faceRect) < 0.0001 || aabb2Height(faceRect) < 0.0001) {
      console.warn("Export: Encountered a face with zero or near-zero area, skipping");
      continue;
    }

    const materialTransformation = Array.isArray(materialTransformations)
      ? materialTransformations[faceIndex]
      : materialTransformations;

    const faceRectPl = polyLine2SetCW(polyLine2FromAABB2(faceRect));
    const faceRectMesh2 = tessPolyLine2(faceRectPl);
    const faceRectMesh3 = mesh2ToMesh3(faceRectMesh2, face.plane, uvGen, materialTransformation);
    const faceRectGlobalUv = faceRectMesh3.uvs1;
    assert(faceRectGlobalUv);
    const faceRectGlobalUvAABB = polyLine2AABB2(faceRectGlobalUv);

    const globalToFaceMat = mat3SolveAffineTransformFromPoints(
      Array.from(faceRectGlobalUv).slice(0, 6),
      Array.from(faceRectPl).slice(0, 6),
    );

    const globalToFaceTransformIsMirrored = mat3IsMirroringAffineTransform(globalToFaceMat);

    // Miter information
    const faceMiterInfo = miters && miters[faceIndex] ? miters[faceIndex] : [];
    const faceMiters: FaceMiter[] = [];
    for (const miterInfo of faceMiterInfo) {
      for (const segment of miterInfo.segments) {
        const faceMiterPlane = planeCopy(miterInfo.miterPlane);

        // Reorient plane for adequate BSP clipping orientation.
        const side = miterPlaneSide(face, faceMiterPlane, segment);
        if (side === null) {
          const [, , z0, , , z1] = segment;
          console.warn("Couldn't resolve miter plane side - miter height=", Math.abs(z1 - z0));
          continue;
        }

        if (side !== PlaneSide.BACK) {
          planeReverse(faceMiterPlane);
        }

        const [x0, y0, x1, y1] = planeProjectPolyLine3(face.plane, polyLine2New(segment));
        faceMiters.push({
          miterPlane: faceMiterPlane,
          line: [x0, y0, x1, y1],
          isInsideCorner: miterInfo.isInsideCorner,
        });
      }
    }

    // Compute cell spans used for generation, merging input cells when the pattern is continuous
    for (let patternIndex = 0; patternIndex < preprocessedPatterns.length; patternIndex++) {
      outputCells[patternIndex] = {};

      if (debugCallback) {
        inputCellFaces[patternIndex] = {};
        outputCellFaces[patternIndex] = {};
        patternSpanFaces[patternIndex] = {};
        patternLoopFaces[patternIndex] = {};
        patternGeometries[patternIndex] = {};
      }
      const pattern = preprocessedPatterns[patternIndex].pattern;
      const direction = preprocessedPatterns[patternIndex].direction;
      if (direction !== SdfDirection.NONE) {
        // TODO: We could just intersect the scanlines with the UV AABB directly instead of first generating tiles along the scanlines and then merging them.
        const tileLocations = sdfTileLocationsInAABB(
          pattern,
          faceRectGlobalUvAABB.minX,
          faceRectGlobalUvAABB.maxX,
          faceRectGlobalUvAABB.minY,
          faceRectGlobalUvAABB.maxY,
        );
        let tileLocation = tileLocations.next();

        const gridToUv = preprocessedPatterns[patternIndex].gridToUvTransform;

        const mergedTileSpans: { [spanId: string]: MergedTileSpan } = {};

        while (!tileLocation.done) {
          const [, , iX, iY] = tileLocation.value as number[];

          if (debugCallback) {
            const tileSpan = polyLine2MakeAxisAlignedRect(iX, iY, iX + 1, iY + 1);
            polyLine2Transform(tileSpan, gridToUv);
            const tileSpanInFace = polyLine2Transform(Float64Array.from(tileSpan), globalToFaceMat);
            const tileFace = faceNew([tileSpanInFace], face.plane);
            inputCellFaces[patternIndex][`x${iX}y${iY}`] = tileFace;
          }

          let mergeKey: string | undefined = undefined;
          let rowIndex: number;
          let mergedTileIndex: number;
          if (direction === SdfDirection.X) {
            mergeKey = `y${iY}`;
            rowIndex = iY;
            mergedTileIndex = iX;
          } else {
            mergeKey = `x${iX}`;
            rowIndex = iX;
            mergedTileIndex = iY;
          }
          if (!mergedTileSpans[mergeKey]) {
            mergedTileSpans[mergeKey] = { rowIndex, minTileInRow: mergedTileIndex, maxTileInRow: mergedTileIndex };
          } else {
            const mergedSpan = mergedTileSpans[mergeKey];
            mergedSpan.minTileInRow = Math.min(mergedSpan.minTileInRow, mergedTileIndex);
            mergedSpan.maxTileInRow = Math.max(mergedSpan.maxTileInRow, mergedTileIndex);
          }

          tileLocation = tileLocations.next();
        }

        const tileRelativeHalfWidth = (0.5 * pattern.xSize) / pattern.xDistance;
        const tileRelativeHalfHeight = (0.5 * pattern.ySize) / pattern.yDistance;
        for (const mergeKey of Object.keys(mergedTileSpans)) {
          const mergedSpan = mergedTileSpans[mergeKey];

          let geometryXScale = 1;
          let geometryYScale = 1;

          let tileRelativeCellLoop: PolyLine2;
          if (direction === SdfDirection.X) {
            tileRelativeCellLoop = polyLine2MakeAxisAlignedRect(
              mergedSpan.minTileInRow,
              mergedSpan.rowIndex + 0.5 - tileRelativeHalfHeight,
              mergedSpan.maxTileInRow + 1,
              mergedSpan.rowIndex + 0.5 + tileRelativeHalfHeight,
            );
            geometryXScale = mergedSpan.maxTileInRow - mergedSpan.minTileInRow + 1;
          } else {
            tileRelativeCellLoop = polyLine2MakeAxisAlignedRect(
              mergedSpan.rowIndex + 0.5 - tileRelativeHalfWidth,
              mergedSpan.minTileInRow,
              mergedSpan.rowIndex + 0.5 + tileRelativeHalfWidth,
              mergedSpan.maxTileInRow + 1,
            );
            geometryYScale = mergedSpan.maxTileInRow - mergedSpan.minTileInRow + 1;
          }

          const tileRelativeAABB = polyLine2AABB2(tileRelativeCellLoop);
          const [gridXCenter, gridYCenter] = aabb2Center(tileRelativeAABB);
          const [uCenter, vCenter] = mat3Dot(gridToUv, gridXCenter, gridYCenter);

          polyLine2Move(tileRelativeCellLoop, -gridXCenter, -gridYCenter);
          const centeredBoundary = polyLine2NonUniformScale(tileRelativeCellLoop, pattern.xDistance, pattern.yDistance);

          outputCells[patternIndex][mergeKey] = {
            centeredBoundary,
            randomValue: 0,
            uCenter,
            vCenter,
            geometryXScale,
            geometryYScale,
          };
        }
      } else {
        const cellLocations: Generator<number[]> = sdfCellBoundariesInAABB(
          sdf.common,
          sdf.randomTiles,
          pattern,
          patternIndex,
          faceRectGlobalUvAABB,
          wrapSizeU !== undefined,
          wrapSizeU,
          wrapSizeV,
        );
        let cellLocation = cellLocations.next();

        const slopeRotation = sdf.common.slopeAxis === "Z" && sdf.common.randomTilesMode === "None" ? pattern.slope : 0;
        const rotationRadians = degreeToRadian(pattern.rotation + slopeRotation);

        while (!cellLocation.done) {
          const [uCenter, vCenter, width, height, cellRand] = cellLocation.value as number[];
          const centeredBoundary = polyLine2MakeAxisAlignedRect(-width * 0.5, -height * 0.5, width * 0.5, height * 0.5);

          const cellKey = `c${cellIndex}`;

          if (debugCallback) {
            const cellSpanInFace = polyLine2Copy(centeredBoundary);
            polyLine2NonUniformScale(cellSpanInFace, width / pattern.xSize, height / pattern.ySize);
            polyLine2Rotate(cellSpanInFace, rotationRadians);
            polyLine2Move(cellSpanInFace, uCenter, vCenter);
            polyLine2Transform(cellSpanInFace, globalToFaceMat);
            const cellFace = faceNew([cellSpanInFace], face.plane);
            inputCellFaces[patternIndex][cellKey] = cellFace;
          }

          outputCells[patternIndex][cellKey] = {
            centeredBoundary,
            randomValue: cellRand,
            uCenter,
            vCenter,
            geometryXScale: width / pattern.xSize,
            geometryYScale: height / pattern.ySize,
          };
          ++cellIndex;

          cellLocation = cellLocations.next();
        }
      }
    }

    // Determine face push-in factor
    //
    // This was copy-pasted logic from `generateMesh3FromDesignMaterial` that needs to be refactored.
    let adjustGeo = false;
    switch (adjustBaseGeo) {
      case "ADJUST_ALL":
        adjustGeo = true;
        break;
      case "ADJUST_EXCLUDING_UPWARDS":
        adjustGeo = vec3Dot(nX, nX, nZ, 0, 0, 1) < 0.1;
        break;
      default:
        adjustGeo = false;
        break;
    }

    let pushInward = 0.0;
    if (adjustGeo) {
      // Push the geometry inward so that the top of the SDF boxes is at the original surface of the geometry.
      for (let i = 0; i < preprocessedPatterns.length; i++) {
        pushInward = Math.max(pushInward, preprocessedPatterns[i].pattern.zSize);
      }
      pushInward *= 1.025; // fudge!
    }
    faceInwardPush.push(pushInward);

    // Generate clipped geometry
    let cellIdForDistortion = 0;
    for (let patternIndex = 0; patternIndex < preprocessedPatterns.length; patternIndex++) {
      const pattern = preprocessedPatterns[patternIndex].pattern;
      const { vertices, indices } = preprocessedPatterns[patternIndex].cellGeometry;
      const { uvs1 } = preprocessedPatterns[patternIndex].cellMesh;

      const slopeRotation = sdf.common.slopeAxis === "Z" && sdf.common.randomTilesMode === "None" ? pattern.slope : 0;
      const [cellEdgeDirU, cellEdgeDirV] = vec2Rotate(1, 0, pattern.rotation + slopeRotation);

      const patternCells = outputCells[patternIndex];
      for (const key of Object.keys(patternCells)) {
        const cell = patternCells[key];
        ++cellIdForDistortion;

        const globalCellBoundingRect = getCellLooseBoundingRect(
          sdf.common,
          sdf.distortions,
          preprocessedPatterns[patternIndex],
          cell,
        );

        const cellLooseBoundingRect = polyLine2Transform(globalCellBoundingRect, globalToFaceMat);

        if (debugCallback) {
          outputCellFaces[patternIndex][key] = faceNew([cellLooseBoundingRect], face.plane);
        }

        if (debugCallback) {
          patternSpanFaces[patternIndex][debugPatternKey(key, 0, null, "patternSpan")] = faceNew(
            [new Float64Array(cellLooseBoundingRect)],
            face.plane,
          );
        }

        // Cut up cell span in individual intersected cells
        let intersectedCells = polygonIntersectSimple(cellLooseBoundingRect, faceOuterLoop);

        let intersectionType: PatternIntersectionType | undefined = undefined;
        const cellLoops: PolyLine2[] = [];
        if (intersectedCells?.length === 0) {
          cellLoops.push(cellLooseBoundingRect);
          intersectionType = PatternIntersectionType.OUTSIDE;
        } else {
          // Weld if pattern span was split into more than one intersected pattern
          if (intersectedCells?.length > 1) {
            const weldedVertices = weldPolyLine2s(intersectedCells, 0.1);
            if (weldedVertices) {
              const postWeldingCells = polygonUnion(intersectedCells.map((o) => [o]));

              intersectedCells = [];
              for (const cell of postWeldingCells) {
                pushArray(intersectedCells, cell);
              }
            }
          }

          // Substract interior loops from these intersected patterns
          for (const intersectedCell of intersectedCells) {
            const clippedCells = polygonSubtract([[intersectedCell], ...faceInnerLoops.map((o) => [o])]);
            for (const culledPattern of clippedCells) {
              pushArray(cellLoops, culledPattern);
            }
          }
        }

        let isStillOriginalCell = false;
        if (cellLoops.length === 1) {
          const originalArea = polyLine2Area(cellLooseBoundingRect);
          const culledArea = polyLine2Area(cellLoops[0]);
          isStillOriginalCell = Math.abs(originalArea - culledArea) / originalArea < 0.01;
        }

        for (let loopIndex = 0; loopIndex < cellLoops.length; loopIndex++) {
          const cellLoop = cellLoops[loopIndex];
          let [cellCenterU, cellCenterV] = mat3Dot(globalToFaceMat, cell.uCenter, cell.vCenter);
          let { geometryXScale, geometryYScale } = cell;
          const mirrorMult = globalToFaceTransformIsMirrored ? -1 : 1;

          // Determine pattern intersection type
          if (intersectionType === PatternIntersectionType.OUTSIDE) {
            if (!debugCallback) {
              continue;
            }
          } else if (isStillOriginalCell) {
            intersectionType = PatternIntersectionType.ORIGINAL;
          } else if (
            preprocessedPatterns[patternIndex].direction !== SdfDirection.NONE &&
            clippedAtEndsOnly(cellLoop, cellLooseBoundingRect, clippingTolerance)
          ) {
            intersectionType = PatternIntersectionType.RECTANGULAR;
            const canonicalizationRect = cellLoop;
            cellCenterU = 0.5 * (canonicalizationRect[0] + canonicalizationRect[4]);
            cellCenterV = 0.5 * (canonicalizationRect[1] + canonicalizationRect[5]);
            // Determine intersected pattern scale and override geometry scale accordingly.
            // This can improve quality of the resulting geometry.
            const canonicalPatternLoop = polyLine2Rotate(
              polyLine2Copy(canonicalizationRect),
              -mirrorMult * degreeToRadian(pattern.rotation), // Slope Z rotation is not possible since pattern is continuous.
              cellCenterU,
              cellCenterV,
            );
            const canonicalPatternAabb = polyLine2AABB2(canonicalPatternLoop);
            if (preprocessedPatterns[patternIndex].direction === SdfDirection.X) {
              geometryXScale = aabb2Width(canonicalPatternAabb) / pattern.xSize;
              geometryYScale = 1;
            } else if (preprocessedPatterns[patternIndex].direction === SdfDirection.Y) {
              geometryXScale = 1;
              geometryYScale = aabb2Height(canonicalPatternAabb) / pattern.ySize;
            }
          } else {
            if (polyLine2IsConvex(cellLoop, DIST_EPSILON, 0.1)) {
              intersectionType = PatternIntersectionType.CONVEX;
            } else {
              intersectionType = PatternIntersectionType.CONCAVE;
            }
          }

          // Find miter lines that touch the pattern loop
          //
          // For now, because we miter with SH (which only works for convex geometry), we can only
          // support miter lines that are collinear to the pattern loop rect.
          //
          // When we have a concave polygon clipper, we could revert to detect miter lines that align
          // with the pattern loop.
          const cellLoopRect = polyLine2FromAABB2(polyLine2AABB2(cellLoop));
          const cellMiters = faceMiters.filter((miterInfo) => {
            const [x0, y0, x1, y1] = miterInfo.line;
            return polyLine2EdgesCollinearToLine(cellLoopRect, x0, y0, x1, y1).length > 0;
          });

          // When we miter at inside corners, we can fix the holes due to material void by
          // expanding the cell away from the center of the cell.
          if (expandCellsAtInsideCornerMiters) {
            const cellMitersAtInsideCorner = cellMiters.filter((m) => m.isInsideCorner);
            if (cellMitersAtInsideCorner.length > 0) {
              const centroid = polyLine2Centroid(cellLoopRect);
              if (centroid !== null) {
                const [cU, cV] = centroid;

                for (const miterInfo of cellMitersAtInsideCorner) {
                  const [x0, y0, x1, y1] = miterInfo.line;
                  const [mU, mV] = [0.5 * (x0 + x1), 0.5 * (y0 + y1)];

                  // TODO: this should be 2 * patternSize, but can't due to our clipper being too aggressive
                  const thickness = 1 * pattern.zSize;

                  const edgeIndices = polyLine2EdgesCollinearToLine(cellLoop, x0, y0, x1, y1);
                  if (edgeIndices.length > 0) {
                    const [dU, dV] = vec2Sub(mU, mV, cU, cV);
                    const [nU, nV] = vec2Normalize(dU, dV);
                    polyLine2MoveEdges(cellLoop, edgeIndices, thickness * nU, thickness * nV);
                  }
                }
              }
            }
          }

          if (debugCallback) {
            patternLoopFaces[patternIndex][debugPatternKey(key, loopIndex, intersectionType, "loop")] = faceNew(
              [new Float64Array(cellLoop)],
              face.plane,
            );
          }

          const cellPushInward =
            pushInward + (cell.randomValue - 0.5) * (pattern.varyHeight ? pattern.varyHeightScale : 0);

          // Transforms are applied in pre-multiplication order (M = M * T), so are specified in last-to-first order.

          // Translate to cell center and push inward
          const cellToFaceUvwMat = mat4Translate(cellCenterU, cellCenterV, -cellPushInward);
          // Scale and rotate
          const mirrorMat = mat4Rotate(mirrorMult * degreeToRadian(pattern.rotation + slopeRotation), 0, 0, 1);
          if (mirrorMat) {
            mat4Mult(cellToFaceUvwMat, mirrorMat, cellToFaceUvwMat);
          }
          mat4Mult(cellToFaceUvwMat, mat4Scale(geometryXScale, geometryYScale, 1), cellToFaceUvwMat);

          // Transformation from face UV to world coordinates
          const cellToWorldMat = mat4Mult(faceUvwToWorldMat, cellToFaceUvwMat);

          // Output original pattern as instances
          if (generatePatternInstances && !sdfDistorted && intersectionType === PatternIntersectionType.ORIGINAL) {
            facePatternInstances[patternIndex].matrices.push(cellToWorldMat);
            continue;
          }

          const cellVertices = new Float32Array(vertices.length);
          for (let vertexIndex = 0; vertexIndex < vertices.length; vertexIndex += 3) {
            const cX = vertices[vertexIndex];
            const cY = vertices[vertexIndex + 1];
            const cZ = vertices[vertexIndex + 2];

            if (sdfDistorted) {
              let [faceX, faceY, faceZ] = mat4Dot(cellToFaceUvwMat, cX, cY, cZ);
              [faceX, faceY, faceZ] = distortSDFVertex(faceX, faceY, faceZ, cellIdForDistortion, sdf.distortions);
              const [rX, rY, rZ] = mat4Dot(faceUvwToWorldMat, faceX, faceY, faceZ);
              cellVertices[vertexIndex] = rX;
              cellVertices[vertexIndex + 1] = rY;
              cellVertices[vertexIndex + 2] = rZ;
            } else {
              const [rX, rY, rZ] = mat4Dot(cellToWorldMat, cX, cY, cZ);
              cellVertices[vertexIndex] = rX;
              cellVertices[vertexIndex + 1] = rY;
              cellVertices[vertexIndex + 2] = rZ;
            }
          }

          let generatedCellMesh = mesh3New(
            new Float32Array(cellVertices),
            new Uint32Array(indices),
            undefined,
            intersectionType === PatternIntersectionType.ORIGINAL ? uvs1 : undefined,
          );

          // Apply clipping
          if (
            intersectionType === PatternIntersectionType.CONVEX ||
            intersectionType === PatternIntersectionType.CONCAVE ||
            (intersectionType !== PatternIntersectionType.OUTSIDE && cellMiters.length > 0)
          ) {
            const clipBSP: NodeBSP | null = computeClipBSP(
              intersectionType,
              cellLoop,
              face.plane,
              nX,
              nY,
              nZ,
              cellEdgeDirU,
              cellEdgeDirV,
              cellCenterU,
              cellCenterV,
            );

            let faces = getFacesFromMesh(generatedCellMesh);
            const outputFaces: Face[] = [];
            splitFacesBSP(faces, {
              node: clipBSP,
              backFaces: outputFaces,
              coplanarsToFront: false,
            });
            faces = outputFaces;

            // Apply mitering after the geometry has been clipped.
            if (cellMiters.length > 0) {
              const miterPlanes = cellMiters.map((miterInfo) => miterInfo.miterPlane);
              faces = clipFacesAgainstPlanes(faces, miterPlanes);
            }

            if (mergeCoplanarFaces) {
              generatedCellMesh = mergeCoplanarMeshFaces({
                faces,
                polygonRoundingMode: PolygonRoundingMode.SNAP_ROUND,
              });
            } else {
              generatedCellMesh = tessFaces3(faces);
            }
          }

          if (intersectionType !== PatternIntersectionType.OUTSIDE) {
            geoBuilderAppend(faceGeoBuilder, {
              vertices: Array.from(generatedCellMesh.verts),
              uvs: generatedCellMesh.uvs1 ? Array.from(generatedCellMesh.uvs1) : [],
              indices: [],
              newIndices: Array.from(generatedCellMesh.tris),
              indicesToTransform: [],
            });
          }

          if (debugCallback) {
            patternGeometries[patternIndex][debugPatternKey(key, loopIndex, intersectionType, "geom")] =
              generatedCellMesh;
          }
        }
      }
    }

    let faceMesh;
    if (!geoBuilderIsEmpty(faceGeoBuilder)) {
      faceMesh = compactMesh3FromGeoBuilder(faceGeoBuilder, { mergeCoplanarFaces: false, generateMissingNormals });
    }
    faceMeshes.push(faceMesh);

    if (debugCallback) {
      debugCallback({
        type: DebugCallbackType.GENERATE_MATERIAL_WITHIN_FACES,
        id: getDebugId().toString(),
        in: faceOrNullCopy(face),
        inputCells: inputCellFaces,
        outputCells: outputCellFaces,
        patternSpans: patternSpanFaces,
        patternLoops: patternLoopFaces,
        patternGeometries: patternGeometries,
      });
      incDebugId();
    }
  }

  return [faceMeshes, faceInwardPush, faceInstances];
}
